// NIGHT-DIRECTSTAFF-2026-10-25 §2: call logging and call-backs. "Log call" on a lead, a candidate or a facility portal contact:
// the outcome (reached / voicemail / no answer / wrong number), one line of note, the next step (call back at <time> / text /
// email / none). Each is ONE activity_log row (no new table, nothing to apply): type 'call_log', the note
//   "Call · <outcome> · next: <step>[ <ISO>][ · note: <line>] · by <staff email>[ · lead_id=<uuid> | · facility_user=<uuid>]"
// (a candidate's row carries candidate_id). A call-back shows on Today from its time until Done; Snooze moves it. Those are rows
// too (type 'call_followup': "Call back done · call_log=<id> · by …" / "Call back snoozed to <ISO> · call_log=<id> · by …", with the
// same lead_id / facility_user tail), so the history keeps every step. Read back here; nothing is ever edited or deleted.
export const CALL_LOG_TYPE = 'call_log';
export const CALL_FOLLOW_TYPE = 'call_followup';
export const CALL_TYPES = [CALL_LOG_TYPE, CALL_FOLLOW_TYPE];
export const OUTCOMES = [['reached', 'Reached them'], ['voicemail', 'Left a voicemail'], ['no_answer', 'No answer'], ['wrong_number', 'Wrong number']];
export const NEXT_STEPS = [['call_back', 'Call back at…'], ['text', 'Text them'], ['email', 'Email them'], ['none', 'Nothing next']];
export const NOTE_MAX = 140;
const OUT_WORDS = Object.fromEntries(OUTCOMES.map(([k]) => [k, k.replace('_', ' ')]));
const WORD_OUT = Object.fromEntries(OUTCOMES.map(([k]) => [k.replace('_', ' '), k]));
const s = (v) => (v === null || v === undefined ? '' : String(v).trim());
const t = (iso) => { const x = Date.parse(iso || ''); return Number.isNaN(x) ? null : x; };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The one line of note, safe in the note's own grammar (no " · "), at most NOTE_MAX characters. */
export const cleanNote = (v) => s(v).replace(/\s+/g, ' ').replace(/·/g, '-').slice(0, NOTE_MAX);

/** What is wrong with the form, in plain words; '' = it can be saved. */
export function callProblem({ outcome, next, at, now = Date.now() } = {}) {
  if (!OUT_WORDS[outcome]) return 'Choose how the call went.';
  if (!NEXT_STEPS.some(([k]) => k === next)) return 'Choose the next step.';
  if (next === 'call_back') {
    const x = t(at);
    if (x === null) return 'Choose when to call back.';
    if (x < now - 5 * 60e3) return 'The call-back time is in the past.';
    if (x > now + 120 * 864e5) return 'Choose a call-back within 120 days.';
  }
  return '';
}

/** ref: { kind: 'lead' | 'candidate' | 'facility', id } -> the tail the timeline finds it by */
const refTail = (ref) => (ref && ref.kind === 'lead' && UUID.test(s(ref.id)) ? ` · lead_id=${s(ref.id)}` : ref && ref.kind === 'facility' && UUID.test(s(ref.id)) ? ` · facility_user=${s(ref.id)}` : '');
export function callNote({ outcome, next, at, note, by, ref }) {
  const step = next === 'call_back' ? `call back at ${new Date(at).toISOString()}` : next;
  const line = cleanNote(note);
  return `Call · ${OUT_WORDS[outcome]} · next: ${step}${line ? ` · note: ${line}` : ''}${by ? ` · by ${by}` : ''}${refTail(ref)}`;
}
const CALL_RE = /^Call · (reached|voicemail|no answer|wrong number) · next: (call back at (\S+)|text|email|none)(?: · note: (.*?))?(?: · by (\S+))?(?: · (lead_id|facility_user)=([0-9a-f-]{36}))?$/i;
/** an activity row -> { id, outcome, next, at, note, by, leadId, facilityUser, candidateId, createdAt } | null */
export function parseCall(a) {
  if (!a || s(a.type) !== CALL_LOG_TYPE) return null;
  const m = CALL_RE.exec(s(a.note));
  if (!m) return null;
  return { id: a.id, outcome: WORD_OUT[m[1].toLowerCase()], next: m[3] ? 'call_back' : m[2], at: m[3] || null, note: m[4] || '', by: m[5] || '',
    leadId: m[6] === 'lead_id' ? m[7] : null, facilityUser: m[6] === 'facility_user' ? m[7] : null, candidateId: a.candidate_id || null, createdAt: a.created_at || a.date };
}
export const followNote = ({ kind, callId, at, by, ref }) => `${kind === 'done' ? 'Call back done' : `Call back snoozed to ${new Date(at).toISOString()}`} · call_log=${callId}${by ? ` · by ${by}` : ''}${refTail(ref)}`;
const FOLLOW_RE = /^Call back (done|snoozed to (\S+)) · call_log=(\d+)/;
export function parseFollow(a) {
  if (!a || s(a.type) !== CALL_FOLLOW_TYPE) return null;
  const m = FOLLOW_RE.exec(s(a.note));
  return m ? { kind: m[1] === 'done' ? 'done' : 'snooze', at: m[2] || null, callId: Number(m[3]), createdAt: a.created_at || a.date } : null;
}

/** The timeline's words for a call row: { title, line } | null */
export function callEntry(a) {
  const c = parseCall(a);
  if (c) {
    const out = OUTCOMES.find(([k]) => k === c.outcome)[1];
    const next = c.next === 'call_back' ? `call back ${new Date(c.at).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} ET`
      : c.next === 'none' ? 'nothing next' : c.next === 'text' ? 'text them next' : 'email them next';
    return { title: `Call logged · ${out}`, line: [next, c.note, c.by ? `by ${c.by}` : ''].filter(Boolean).join(' · ') };
  }
  const f = parseFollow(a);
  if (f) return { title: f.kind === 'done' ? 'Call back done' : 'Call back snoozed', line: f.kind === 'done' ? '' : `to ${new Date(f.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })} ET` };
  return null;
}

/**
 * Today's "Call backs due": each logged call with a call-back whose time (the newest snooze, else the one set) has come and that
 * is not Done. -> [{ key, call, due, who }] oldest due first. who(call) -> { name, phone, open } from the caller's lists (or null).
 */
export function callBacksDue({ activity = [], now = Date.now(), who = () => null } = {}) {
  const calls = activity.map(parseCall).filter((c) => c && c.next === 'call_back' && c.at);
  const follows = activity.map(parseFollow).filter(Boolean);
  const out = [];
  for (const c of calls) {
    const mine = follows.filter((f) => f.callId === Number(c.id)).sort((a, b) => s(a.createdAt).localeCompare(s(b.createdAt)));
    if (mine.some((f) => f.kind === 'done')) continue;
    const snooze = mine.filter((f) => f.kind === 'snooze').pop();
    const due = snooze ? snooze.at : c.at;
    if ((t(due) ?? Infinity) > now) continue;
    const w = who(c);
    if (w === false) continue;                                        // the person is test / merged / gone: never listed
    out.push({ key: c.id, call: c, due, who: w || null });
  }
  return out.sort((a, b) => (t(a.due) ?? 0) - (t(b.due) ?? 0));
}

/** Was a call logged for this lead after `sinceIso`? (a voicemail / missed call is then "called back") */
export const calledSince = (activity, leadId, sinceIso) => (activity || []).some((a) => {
  const c = parseCall(a);
  return c && c.leadId && s(c.leadId).toLowerCase() === s(leadId).toLowerCase() && (t(c.createdAt) ?? 0) >= (t(sinceIso) ?? Infinity);
});

/** Snooze choices from now: one hour, or tomorrow 9 AM Eastern */
export function snoozeTimes(now = Date.now()) {
  const etDay = new Date(now + 864e5).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  // 9 AM Eastern tomorrow: try both offsets and keep the one that reads 09:00 in New York
  const nine = [-4, -5].map((h) => Date.parse(`${etDay}T09:00:00${h === -4 ? '-04:00' : '-05:00'}`))
    .find((x) => new Date(x).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }) === '09:00');
  return [['1h', 'In 1 hour', new Date(now + 36e5).toISOString()], ['tomorrow', 'Tomorrow 9 AM', new Date(nine).toISOString()]];
}

export function makeCallsApi(supabase) {
  const by = async () => { try { const { data } = await supabase.auth.getUser(); return (data && data.user && data.user.email) || ''; } catch (e) { return ''; } };
  const insert = async (row) => {
    const { data, error } = await supabase.from('activity_log').insert(row).select('id,candidate_id,type,note,date,created_at').limit(1);
    if (error) throw new Error(`Not saved: ${error.message}`);
    return (data || [])[0] || row;
  };
  const base = (ref) => ({ candidate_id: ref && ref.kind === 'candidate' ? ref.id : null, date: new Date().toISOString().split('T')[0] });
  return {
    /** -> the saved row */
    async log({ ref, outcome, next, at, note }) {
      const why = callProblem({ outcome, next, at });
      if (why) throw new Error(why);
      return insert({ ...base(ref), type: CALL_LOG_TYPE, note: callNote({ outcome, next, at, note, by: await by(), ref }) });
    },
    async done(call, ref) { return insert({ ...base(ref), type: CALL_FOLLOW_TYPE, note: followNote({ kind: 'done', callId: call.id, by: await by(), ref }) }); },
    async snooze(call, at, ref) { return insert({ ...base(ref), type: CALL_FOLLOW_TYPE, note: followNote({ kind: 'snooze', callId: call.id, at, by: await by(), ref }) }); },
    /** the last 90 days of call rows (Today and the voicemail lines) -> [] on any failure */
    async recent(now = Date.now()) {
      try {
        const { data, error } = await supabase.from('activity_log').select('id,candidate_id,type,note,date,created_at').in('type', CALL_TYPES)
          .gte('created_at', new Date(now - 90 * 864e5).toISOString()).order('created_at', { ascending: true }).limit(5000);
        return error ? [] : (data || []);
      } catch (e) { return []; }
    },
  };
}

/** The ref a follow-up row carries (the same person as the call) */
export const refOfCall = (c) => (c.leadId ? { kind: 'lead', id: c.leadId } : c.facilityUser ? { kind: 'facility', id: c.facilityUser } : c.candidateId ? { kind: 'candidate', id: c.candidateId } : null);
